import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HardDrive, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * 🆕 Widget spazio disco per Dashboard.
 * Mostra: MB usati / limite + progress bar + avviso quando lo spazio sta per finire.
 */
export function StorageUsageWidget({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    const load = async () => {
      try {
        const { data } = await api.get("/agency/storage-usage");
        if (ok) setUsage(data);
      } catch { /* fail silent */ }
      if (ok) setLoading(false);
    };
    load();
    const iv = setInterval(load, 60000); // refresh ogni minuto
    return () => { ok = false; clearInterval(iv); };
  }, []);

  if (loading || !usage) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse" data-testid="storage-widget-loading">
        <div className="h-4 w-24 bg-slate-200 rounded mb-3" />
        <div className="h-8 w-32 bg-slate-200 rounded mb-2" />
        <div className="h-2 w-full bg-slate-100 rounded" />
      </div>
    );
  }

  const { used_mb, limit_mb, used_percent, level, count } = usage;
  const barColor = level === "critical" ? "bg-rose-500" : level === "warning" ? "bg-amber-500" : "bg-emerald-500";
  const iconColor = level === "critical" ? "text-rose-600" : level === "warning" ? "text-amber-600" : "text-emerald-600";
  const bgColor = level === "critical" ? "bg-rose-50 border-rose-200" : level === "warning" ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200";

  // 🆕 Modalità compatta ULTRA-MINI: badge singola riga per la Dashboard
  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 rounded-md border ${bgColor} px-2.5 py-1 text-xs transition-colors`} data-testid="storage-widget">
        <HardDrive className={`h-3.5 w-3.5 ${iconColor}`} />
        <span className="tabular-nums font-medium text-slate-700" data-testid="storage-widget-used">
          {used_mb.toFixed(1)}
        </span>
        <span className="text-slate-400">/ {limit_mb} MB</span>
        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden" role="progressbar" aria-valuenow={used_percent} aria-valuemin={0} aria-valuemax={100}>
          <div className={`h-full ${barColor}`} style={{ width: `${Math.min(used_percent, 100)}%` }} data-testid="storage-widget-bar" />
        </div>
        <span
          className={`tabular-nums font-semibold ${level === "critical" ? "text-rose-700" : level === "warning" ? "text-amber-700" : "text-emerald-700"}`}
          data-testid="storage-widget-percent"
        >
          {used_percent.toFixed(1)}%
        </span>
      </div>
    );
  }

  return (
    <div className={`rounded-lg p-4 border ${bgColor} transition-colors`} data-testid="storage-widget">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-2">
          <HardDrive className={`h-4 w-4 ${iconColor}`} />
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Spazio archiviazione</span>
        </div>
        <span className="text-[10px] text-slate-500 tabular-nums" data-testid="storage-widget-count">{count} file</span>
      </div>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
        <div>
          <span className="text-xl font-bold tabular-nums text-slate-800" data-testid="storage-widget-used">{used_mb.toFixed(1)} MB</span>
          <span className="text-xs text-slate-500 ml-1">/ {limit_mb} MB</span>
        </div>
        <span
          className={`text-sm font-bold tabular-nums ${level === "critical" ? "text-rose-700" : level === "warning" ? "text-amber-700" : "text-emerald-700"}`}
          data-testid="storage-widget-percent"
        >
          {used_percent.toFixed(1)}%
        </span>
      </div>
      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden" role="progressbar" aria-valuenow={used_percent} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.min(used_percent, 100)}%` }}
          data-testid="storage-widget-bar"
        />
      </div>
      {!compact && (
        <div className="mt-2 text-[11px]">
          {level === "critical" && (
            <div className="flex items-start gap-1.5 text-rose-800" data-testid="storage-widget-alert-critical">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Spazio quasi esaurito! Rimuovi file inutili o aggiorna il piano.</span>
            </div>
          )}
          {level === "warning" && (
            <div className="flex items-start gap-1.5 text-amber-800" data-testid="storage-widget-alert-warning">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Attenzione: hai usato oltre l&apos;80% dello spazio.</span>
            </div>
          )}
          {level === "ok" && (
            <div className="flex items-start gap-1.5 text-emerald-700" data-testid="storage-widget-alert-ok">
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Spazio disponibile abbondante.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StorageUsageWidget;
